import { React, useState, useEffect, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { Formik, Form, ErrorMessage } from 'formik'
import * as yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
// import { postTaxData } from "services/api/modules/staff-management/staffManagementDashboard";
import {
  postPayrollPersonalDetails,
  recalculateSelectedEmployee,
} from 'services/api/modules/payroll/payroll-view'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

const PersonalDetails = ({ data, time_id, employeeId, personalDeets }) => {
  const [disable, setDisable] = useState(true)
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [isLoading, setIsLoading] = useState(false)

  const [initialValues, setInitialValues] = useState({
    resident_status: '',
    disability_status: null,
    income_tax_number: '',
  })

  const validationSchema = yup.object().shape({
    resident_status: yup.number().nullable().required('Please select a status'),
    disability_status: yup.string().nullable().required('Disability Status is required'),
    income_tax_number: yup
      .string()
      .nullable()
      // .required("Please insert the income tax number")
      .matches(/^[a-z\d]+$/i, 'Income Tax Number must be alphanumeric'),
  })

  const taxResidencyStatus = [
    {
      id: 1,
      description: 'Resident',
    },
    {
      id: 2,
      description: 'Non-resident',
    },
    {
      id: 3,
      description: 'Returning Expert Program (REP)',
    },
    {
      id: 4,
      description: 'Knowledge worker (IRDA)',
    },
    {
      id: 5,
      description: 'C suites',
    },
  ]

  // RECALCULATE
  const { mutate: recalculatePayroll } = useMutation(recalculateSelectedEmployee, {
    onSuccess: (res) => {
      setIsLoading(false)
      setDisable(true)
      queryClient.invalidateQueries('retrieveEmployeeData')
      queryClient.invalidateQueries('employeeTaxData')
      queryClient.invalidateQueries('employeePersonalData')
      // navigate(-1);
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        console.log(error)
      }
    },
  })

  const mutation = useMutation((value) => postPayrollPersonalDetails(employeeId, value), {
    onSuccess: (response) => {
      setIsLoading(true)
      // alert('Recalculate payroll for selected employee');
      recalculatePayroll({ time_id, employee_id: employeeId, data: {} })
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        console.log(error)
      }
    },
  })

  const handleSubmitTax = (values, action) => {
    mutation.mutate(values)
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  useEffect(() => {
    if (data && personalDeets) {
      setInitialValues({
        resident_status: data?.data?.resident_status,
        disability_status: personalDeets?.data?.disability_status,
        income_tax_number: data?.data?.income_tax_number,
      })
    }
  }, [data, personalDeets])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitTax}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, resetForm }) => {
        return (
          <Form>
            <Box
              p={1.75}
              display="flex"
              justifyContent="space-between"
              borderBottom="1px solid"
              sx={{ borderColor: 'gray-line.main' }}
              alignItems="center"
            >
              <Typography color="light-gray-text.main" variant="subtitle2" fontWeight={500}>
                Personal details
              </Typography>
              {disable && (
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    color: 'alternate.main',
                    cursor: 'pointer',
                  }}
                  onClick={() => setDisable(false)}
                >
                  Edit
                </Typography>
              )}
            </Box>
            <Box p={1.75} mt={2}>
              <Box display="flex" alignItems="flex-start" columnGap={4} width={1} sx={{ mb: 2.5 }}>
                <Box width={1} sx={{ width: { xs: '100%', sm: '100%' } }}>
                  <AdxDropdown
                    label="Tax Residency Status"
                    name="resident_status"
                    disabled={disable}
                    sxLabel={{
                      color: disable
                        ? 'light-gray-text.main'
                        : 'dark-text.main' /*, fontSize: "30px!important" */,
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Salary category"
                    value={values.resident_status}
                    onChange={handleChange}
                    errorMessage={
                      <ErrorMessage name="resident_status" component={AdxErrorMessage} />
                    }
                    isErrorShown={touched.resident_status && errors.resident_status}
                  >
                    {taxResidencyStatus.map((resident, index) => (
                      <AdxMenuItem
                        key={index}
                        value={resident.id}
                        selectedvalue={values.resident_status}
                      >
                        {resident.description}
                      </AdxMenuItem>
                    ))}
                  </AdxDropdown>
                </Box>
                <Box width={1} sx={{ width: { xs: '100%', sm: '100%' } }}>
                  <AdxDropdown
                    placeholder="Disability Status"
                    label="Disability Status"
                    disabled={disable}
                    sxLabel={{
                      color: disable
                        ? 'light-gray-text.main'
                        : 'dark-text.main' /*, fontSize: "30px!important" */,
                    }}
                    sxDropdown={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    name="disability_status"
                    value={values.disability_status}
                    onChange={handleChange}
                    errorMessage={
                      <ErrorMessage name="disability_status" component={AdxErrorMessage} />
                    }
                    isErrorShown={touched.disability_status && errors.disability_status}
                  >
                    <AdxMenuItem value={1} selectedvalue={values.disability_status}>
                      {'Yes'}
                    </AdxMenuItem>
                    <AdxMenuItem value={0} selectedvalue={values.disability_status}>
                      {'No'}
                    </AdxMenuItem>
                  </AdxDropdown>
                </Box>
              </Box>
              <Box gap={2.5} display="flex" justifyContent="space-between" mb={2.5}>
                <AdxInput
                  label="Income Tax Number"
                  name="income_tax_number"
                  disabled={disable}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Income Tax Number"
                  value={values.income_tax_number}
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="income_tax_number" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.income_tax_number && errors.income_tax_number}
                  sx={{ width: '100%' }}
                />
              </Box>
              {!disable && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: 1,
                  }}
                >
                  <AdxButton
                    variant={'outlined'}
                    size="small"
                    onClick={() => cancelEditForm(resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  {isLoading ? (
                    <AdxButton variant={'contained'} size="small" sx={{ width: 0.2 }}>
                      <CircularProgress color="white" size={20} />
                    </AdxButton>
                  ) : (
                    <AdxButton
                      variant={'contained'}
                      size="small"
                      onClick={() => handleCheckRef(handleSubmit)}
                      sx={{ width: 0.2 }}
                    >
                      Save
                    </AdxButton>
                  )}
                </Box>
              )}
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}

export default PersonalDetails
